import React, {Fragment, useState} from 'react';
import {Stack, Typography, useTheme, Box, Skeleton, Button} from '@mui/material';

import {
	AlertInfo,
	AlertModal,
	CTAButtonFixed,
	HeaderTabNavigation,
	IFrameModal,
} from 'presentation/components';
import {Loading} from 'presentation/components/Loading/Loading';
import {CustomCTAButtonFixedAction} from 'presentation/pages/private/authorizations/styles';
import {translations} from 'presentation/translations-new';

import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {BillStatusEnum, FeaturesEnum} from 'presentation/constants';
import {RecurrenceTypeEnum} from 'domain/usecases';
import {OpenBillsTutorial} from './components/OpenBillsTutorial';
import {formatPriceBill} from 'presentation/utils';
import {FinancialCardV2} from 'presentation/pages/private/home/components/FinancialCardV2';

import {ProductLogo} from 'presentation/components/ProductLogo';
import {BillsErrorState} from '../../components/BillsErrorState';
import {OlderBillsModal} from './components/OlderBillsModal';
import {PaymentErrorModal} from './components/PaymentErrorModal';
import {DetailsDrawer} from './components/DetailsDrawer';
import {OpenBillsEmptyState} from './components/EmptyState';
import {OpenBillsTable} from './components/OpenBillsTable';
import {PaymentSummaryCards} from './components/PaymentSummaryCards';
import {AutoPaymentCard} from './components/AutoPaymentCard';
import {useOpenBillsActions} from './hooks/useOpenBillsActions';
import {useBemobiPostMessage} from './hooks/useBemobiPostMessage';
import {OpenBillStatusEnum} from './components/OpenBillsTable/types';
import {Container} from '../../styles';
import {SubscriptionAlert} from './components/SubscriptionAlert';
import {useAccountInformation} from 'presentation/pages/private/hooks';
import {OldOpenBills} from './OldOpenBills';

const {
	delayPaymentAttention,
	selectBillsToPay,
	totalSelectedBills,
	pay,
	noSelectionModal,
	closeIframeConfirmModal,
} = translations['pt-br'].privateFlow.myFinancial.openBills;

const {paySelectedBills} =
	translations['pt-br'].privateFlow.myFinancial.openBills.olderInvoicesModal;

function OpenBillsPage() {
	const {palette} = useTheme();
	const {
		autoPaymentType,
		rows,
		newBillsFlagIsEnabled,
		isMobile,
		isLoading,
		isError,
		isPayLoading,
		isPayError,
		noSelectionModalOpen,
		olderBillsModalOpen,
		olderBillsCurrentRow,
		numberOfOverdueBills,
		isMultiSelectionOlderBills,
		isEmpty,
		tabs,
		drawerOpen,
		selectedRow,
		selectedIds,
		hasPendingInitialDrawer,
		handleSelectionChange,
		handleToggleRow,
		handleViewDetails,
		handleCloseDrawer,
		handlePay,
		handlePayFromDrawer,
		handleGoToOldestBill,
		handleContinueWithCurrent,
		handleTryAgain,
		setIsPayError,
		setNoSelectionModalOpen,
		setOlderBillsModalOpen,
		checkoutUrl,
		isIframeOpen,
		setIsIframeOpen,
		recurrenceUrl,
		isRecurrenceIframeOpen,
		setIsRecurrenceIframeOpen,
		handleAutoPaymentEnable,
		handleAutoPaymentChange,
		handleAutoPaymentDeactivate,
		isAutoPaymentLoading,
	} = useOpenBillsActions();
	const {isHolder} = useAccountInformation();
	const {isEnabled: isTutorialEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_NEW_BILLS_BEMOBI_TUTORIAL,
	);

	const [isCloseIframeConfirmOpen, setIsCloseIframeConfirmOpen] = useState(false);

	const {handleBemobiPostMessage} = useBemobiPostMessage({setIsIframeOpen, setIsPayError});

	const handleCloseIframeConfirm = () => {
		setIsCloseIframeConfirmOpen(false);
		setIsIframeOpen(false);
		window.history.back();
	};

	const shouldShowTutorial =
		!isLoading &&
		isTutorialEnabled &&
		!drawerOpen &&
		!hasPendingInitialDrawer &&
		!isIframeOpen &&
		!isRecurrenceIframeOpen;

	const renderBillsSection = () => {
		if (isLoading) {
			if (isMobile) {
				return (
					<Stack gap={1} pb={10}>
						{Array.from({length: 3}).map((_, i) => (
							<Skeleton key={i} height={148} sx={{transform: 'initial'}} />
						))}
					</Stack>
				);
			}
			return <Skeleton height={328} sx={{transform: 'initial'}} />;
		}

		if (isMobile) {
			return (
				<Stack id="open-bills-cards-mobile" gap={1} pb={16}>
					{rows.map((row, index) => (
						<div
							key={row.cdObrigacao}
							id={index === 0 ? 'open-bills-first-card-mobile' : undefined}>
							<FinancialCardV2
								checkboxId={
									index === 0
										? 'open-bills-first-card-checkbox-mobile'
										: undefined
								}
								hideInterestFines
								selected={selectedIds.has(row.cdObrigacao)}
								onSelect={
									!row.isSaoLucas && row.cdEspecieTitulo !== 70
										? () => handleToggleRow(row.cdObrigacao)
										: undefined
								}
								checkboxDisabled={
									row.isSaoLucas || row.cdEspecieTitulo === 70
								}
								onViewDetails={() => handleViewDetails(row)}
								bill={{
									dtPrevista: row.dtPrevista,
									cdObrigacao: Number(row.cdObrigacao),
									cdObrigacaoOrigem: Number(row.cdObrigacao),
									status:
										row.status === OpenBillStatusEnum.OVERDUE
											? BillStatusEnum.EXPIRED
											: BillStatusEnum.OPEN,
									flTipoPdf: false,
									flMigracao: false,
									linhaDigitavel: {
										vlObrigacao: String(
											row.status === OpenBillStatusEnum.OVERDUE
												? (row.vlAtualizado ?? row.vlObrigacao)
												: row.vlObrigacao,
										),
										dtVencimento: row.dtVencimento,
									},
									cdEspecieTitulo: row.cdEspecieTitulo,
								}}
							/>
						</div>
					))}
				</Stack>
			);
		}

		return (
			<OpenBillsTable
				rows={rows}
				selectedIds={selectedIds}
				onSelectionChange={handleSelectionChange}
				onViewDetails={handleViewDetails}
			/>
		);
	};

	if (!newBillsFlagIsEnabled) {
		return <OldOpenBills />;
	}

	if (isError) {
		return (
			<Stack>
				<HeaderTabNavigation variant={isMobile ? 'fullWidth' : 'scrollable'} tabs={tabs} />
				<BillsErrorState handleRefresh={handleTryAgain} />
			</Stack>
		);
	}

	if (isEmpty) {
		return (
			<Fragment>
				<Stack>
					<HeaderTabNavigation
						variant={isMobile ? 'fullWidth' : 'scrollable'}
						tabs={tabs}
					/>
					<Container>
						<Stack gap={1.5}>
							<AutoPaymentCard
								type={autoPaymentType}
								onEnable={handleAutoPaymentEnable}
								onChange={handleAutoPaymentChange}
								onDeactivate={handleAutoPaymentDeactivate}
								isEnableLoading={isAutoPaymentLoading}
								isChangeLoading={isAutoPaymentLoading}
								isDeactivateLoading={isAutoPaymentLoading}
							/>
							<OpenBillsEmptyState />
						</Stack>
					</Container>
				</Stack>
				<IFrameModal
					openIframe={isRecurrenceIframeOpen}
					setOpenIframe={setIsRecurrenceIframeOpen}
					url={recurrenceUrl ?? undefined}
					title={<ProductLogo invertColor />}
				/>
				<PaymentErrorModal open={isPayError} onClose={() => setIsPayError(false)} />
			</Fragment>
		);
	}

	return (
		<Fragment>
			{shouldShowTutorial ? (
				<OpenBillsTutorial
					isMobile={isMobile}
					hasSelectableRow={rows.length > 0 && !rows[0].isSaoLucas}
				/>
			) : null}
			<Stack>
				<HeaderTabNavigation
					headerId="open-bills-tabs-header"
					variant={isMobile ? 'fullWidth' : 'scrollable'}
					tabs={tabs}
				/>
				<Container>
					<Stack gap={1.5}>
						{isHolder ? <SubscriptionAlert isBemobiFlow /> : null}
						<AlertInfo.Root severity="warning">
							<AlertInfo.Description>{delayPaymentAttention}</AlertInfo.Description>
						</AlertInfo.Root>
						<PaymentSummaryCards
							isLoading={isLoading}
							isMobile={isMobile}
							rows={rows}
							selectedIds={selectedIds}
							isPayLoading={isPayLoading}
							onPay={handlePay}
							autoPaymentType={autoPaymentType}
							onAutoPaymentEnable={handleAutoPaymentEnable}
							onAutoPaymentChange={handleAutoPaymentChange}
							onAutoPaymentDeactivate={handleAutoPaymentDeactivate}
							isAutoPaymentLoading={isAutoPaymentLoading}
						/>
						<Typography variant="body1" color="text.secondary" fontWeight={600}>
							{selectBillsToPay}
						</Typography>
						{renderBillsSection()}
					</Stack>
				</Container>
			</Stack>
			{isMobile ? (
				<CTAButtonFixed.Root
					flexDirection="row"
					alignItems="center"
					justifyContent="space-between"
					gap={2}>
					<Stack gap={0}>
						<Typography variant="caption" color="text.secondary">
							{totalSelectedBills}
						</Typography>
						<Box display="flex" alignItems="center" gap={0.5}>
							<Typography fontWeight={600} variant="h6" color="text.primary">
								{formatPriceBill(
									rows
										.filter(r => selectedIds.has(r.cdObrigacao))
										.reduce(
											(acc, r) =>
												acc +
												(r.status === OpenBillStatusEnum.OVERDUE
													? (r.vlAtualizado ?? r.vlObrigacao)
													: r.vlObrigacao),
											0,
										),
								)}
							</Typography>
							{rows.some(
								r =>
									selectedIds.has(r.cdObrigacao) &&
									r.status === OpenBillStatusEnum.OVERDUE,
							) ? (
								<Typography
									variant="body1"
									fontWeight={600}
									color={palette.error.main}>
									*
								</Typography>
							) : null}
						</Box>
					</Stack>
					<CustomCTAButtonFixedAction
						variant="contained"
						sx={{flex: 1, maxWidth: 103}}
						disabled={isPayLoading}
						endIcon={isPayLoading ? <Loading size={16} color="inherit" /> : null}
						onClick={handlePay}>
						{pay}
					</CustomCTAButtonFixedAction>
				</CTAButtonFixed.Root>
			) : null}
			<DetailsDrawer
				open={drawerOpen}
				onClose={handleCloseDrawer}
				row={selectedRow}
				onPayBill={handlePayFromDrawer}
				hasAutoPayment={autoPaymentType !== RecurrenceTypeEnum.BILL}
				isPayLoading={isPayLoading}
			/>
			<PaymentErrorModal open={isPayError} onClose={() => setIsPayError(false)} />
			<IFrameModal
				openIframe={isIframeOpen}
				setOpenIframe={setIsIframeOpen}
				url={checkoutUrl ?? undefined}
				title={<ProductLogo invertColor />}
				allow="payment; clipboard-read; clipboard-write; publickey-credentials-get; publickey-credentials-create;"
				onRequestClose={() => setIsCloseIframeConfirmOpen(true)}
				onPostMessage={handleBemobiPostMessage}
			/>
			<IFrameModal
				openIframe={isRecurrenceIframeOpen}
				setOpenIframe={setIsRecurrenceIframeOpen}
				url={recurrenceUrl ?? undefined}
				title={<ProductLogo invertColor />}
			/>
			<OlderBillsModal
				open={olderBillsModalOpen}
				onClose={() => setOlderBillsModalOpen(false)}
				numberOfOverdueBills={numberOfOverdueBills}
				currentBill={olderBillsCurrentRow}
				onGoToOldestBill={handleGoToOldestBill}
				onContinueWithCurrent={handleContinueWithCurrent}
				continueButtonLabel={isMultiSelectionOlderBills ? paySelectedBills : undefined}
			/>
			<AlertModal
				open={isCloseIframeConfirmOpen}
				onClose={() => setIsCloseIframeConfirmOpen(false)}
				variant="info"
				customMaxWidth={isMobile ? 328 : 420}
				sx={{zIndex: 10000}}
				headerTitle={closeIframeConfirmModal.headerTitle}
				title={closeIframeConfirmModal.title}
				description={closeIframeConfirmModal.description}
				footerContent={
					<Stack width="100%" gap={1}>
						<Button
							size="large"
							variant="contained"
							fullWidth
							onClick={handleCloseIframeConfirm}>
							{closeIframeConfirmModal.confirm}
						</Button>
						<Button
							size="large"
							variant="outlined"
							fullWidth
							onClick={() => setIsCloseIframeConfirmOpen(false)}>
							{closeIframeConfirmModal.cancel}
						</Button>
					</Stack>
				}
			/>
			<AlertModal
				open={noSelectionModalOpen}
				onClose={() => setNoSelectionModalOpen(false)}
				variant="info"
				customMaxWidth={328}
				headerTitle={noSelectionModal.headerTitle}
				title={noSelectionModal.title}
				footerContent={
					<Stack width="100%">
						<Button
							size="large"
							variant="contained"
							fullWidth
							onClick={() => setNoSelectionModalOpen(false)}>
							{noSelectionModal.confirm}
						</Button>
					</Stack>
				}
			/>
		</Fragment>
	);
}

export {OpenBillsPage};
